import React, { createContext, useContext, ReactNode, Suspense } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider } from '@mui/material';
import { CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router-dom';
import { createLibraryStore, ApiClientConfig } from '../../redux';
import { createLightTheme, createDarkTheme, Color } from '../../theme';
import { AuthProvider } from '@erpsquad/common/runtime';
import { LanguageProvider } from '@erpsquad/common/runtime';
import { PageProvider } from '@erpsquad/common/runtime';
import { PermissionsProvider } from '@erpsquad/common/runtime';
import MUIThemeWrapper from '../../theme/themeWrapper';
import Loader from '../loader';
import useAppTranslations from '../../hooks/use-translations';
import '../../utils/i18n';
import { initializeApiConfig, ApiConfig } from '../../utils/api-config';
import { initializeApiBaseUrls, extendApiConfigurations } from '../../utils/api';
import { setModuleRoutes } from '../../utils/module-routes-config';
import { useSnackbarRef } from '../../hooks/apiHelper';

const SnackbarRefTracker: React.FC = () => {
  useSnackbarRef();
  return null;
};


// Context for library configuration
interface ERPUIContextValue {
  useRedux: boolean;
  apiClient?: ApiClientConfig;
  theme?: any;
  themeMode: 'light' | 'dark';
  direction: 'ltr' | 'rtl';
  primaryColor?: Color;
}

const ERPUIContext = createContext<ERPUIContextValue>({
  useRedux: false,
  themeMode: 'light',
  direction: 'ltr',
});

export const useERPUI = () => useContext(ERPUIContext);

// Provider props interface
export interface ERPUIProviderProps {
  children: ReactNode;

  // Redux configuration
  useRedux?: boolean;
  reduxStore?: any;
  apiClient?: ApiClientConfig;
  additionalReducers?: Record<string, any>;

  // Theme configuration
  theme?: any;
  themeMode?: 'light' | 'dark';
  primaryColor?: Color;
  direction?: 'ltr' | 'rtl';

  // Context providers configuration
  enableAuth?: boolean;
  enableLanguage?: boolean;
  enablePages?: boolean;
  enablePermissions?: boolean;
  enableRouter?: boolean;
  enableSnackbar?: boolean;

  // Other configuration
  enableCssBaseline?: boolean;
  snackbarConfig?: {
    maxSnack?: number;
    anchorOrigin?: {
      vertical: 'top' | 'bottom';
      horizontal: 'left' | 'center' | 'right';
    };
    autoHideDuration?: number;
  };

  // API configuration
  apiConfig?: ApiConfig;

  // Additional API configurations to extend the shared apiConfigurations map
  additionalApiConfigurations?: Record<string, any>;

  // Module routes override — allows MFE apps to inject their own full route set
  // so the header module dropdown can match routes correctly.
  // Example: { hrms: Object.values(PathnameHrms).map(p => Pathname.DASHBOARD_HRMS + p) }
  moduleRoutes?: Record<string, string[]>;

  // Route maps
  routeToColumnsMap?: Record<string, any>;
  routeToResourceMap?: Record<string, any>;
  publicRoutes?: string[]; // Optional prop to allow customization of public routes in AuthProvider

  // Maps module names (e.g. 'accounting') to the currency_data field used for
  // fraction precision in that module. Example: { accounting: 'accounting_fraction_unit' }
  moduleToFractionFieldMap?: Record<string, string>;
}

export const ERPUIProvider: React.FC<ERPUIProviderProps> = ({
  children,

  // Redux props
  useRedux = false,
  reduxStore,
  apiClient,
  additionalReducers = {},

  // Theme props
  theme: customTheme,
  themeMode = 'light',
  primaryColor,
  direction = 'ltr',

  // Context providers configuration
  enableAuth = true,
  enableLanguage = true,
  enablePages = true,
  enablePermissions = true,
  enableRouter = false, // Default false since consuming app usually provides router
  enableSnackbar = true,

  // Other props
  enableCssBaseline = true,
  snackbarConfig = {
    maxSnack: 3,
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    autoHideDuration: 5000,
  },

  // API configuration
  apiConfig: providedApiConfig,
  additionalApiConfigurations,
  moduleRoutes,
  routeToColumnsMap,
  routeToResourceMap,
  publicRoutes = [], // Default empty array for public routes in AuthProvider
  moduleToFractionFieldMap,
}) => {
  // Initialize API configuration synchronously before first render
  // This prevents "API configuration not initialized" errors when components
  // try to use the API during initial render
  const isInitialized = React.useRef(false);

  if (providedApiConfig && !isInitialized.current) {
    try {
      initializeApiConfig(providedApiConfig);
      initializeApiBaseUrls();
      if (additionalApiConfigurations) {
        extendApiConfigurations(additionalApiConfigurations);
      }
      // Inject MFE-provided module routes so the header can resolve modules
      if (moduleRoutes) {
        setModuleRoutes(moduleRoutes);
      }
      // Store module-to-fraction-field mapping so utility functions can resolve
      // the correct currency fraction field per module (e.g. accounting_fraction_unit).
      if (moduleToFractionFieldMap) {
        localStorage.setItem('_module_fraction_map', JSON.stringify(moduleToFractionFieldMap));
      }
      isInitialized.current = true;
    } catch (error) {
      console.error('Failed to initialize API configuration:', error);
    }
  }
  // const { isLoading } = useAppTranslations();
  // if (isLoading) {
  //   return <Loader />;
  // }
  // Create or use provided Redux store
  const store = React.useMemo(() => {
    if (!useRedux) return null;

    if (reduxStore) {
      return reduxStore;
    }

    return createLibraryStore(additionalReducers, apiClient);
  }, [useRedux, reduxStore, additionalReducers, apiClient]);

  // Create theme
  const theme = React.useMemo(() => {
    if (customTheme) {
      return customTheme;
    }

    const themeCreator = themeMode === 'dark' ? createDarkTheme : createLightTheme;
    const { theme: generatedTheme } = themeCreator(primaryColor, direction);
    return generatedTheme;
  }, [customTheme, themeMode, primaryColor, direction]);

  // Context value
  const contextValue: ERPUIContextValue = {
    useRedux,
    apiClient,
    theme,
    themeMode,
    direction,
    primaryColor,
  };

  // Build provider tree from inside out
  let content = (
    <ERPUIContext.Provider value={contextValue}>
      {/* <ThemeProvider theme={theme}>
        {enableCssBaseline && <CssBaseline />} */}
      {/* <Suspense fallback={<Loader />}> */}
      <MUIThemeWrapper theme={theme} enableCssBaseline={enableCssBaseline}>
        {children}
      </MUIThemeWrapper>
      {/* </Suspense> */}
      {/* </ThemeProvider> */}
    </ERPUIContext.Provider>
  );

  // Wrap with snackbar provider if enabled
  if (enableSnackbar) {
    content = (
      <SnackbarProvider {...snackbarConfig}>
        <SnackbarRefTracker />
        {content}
      </SnackbarProvider>
    );
  }

  // Wrap with page provider if enabled
  // if (enablePages) {
  //   content = (
  //     <PageProvider routeToColumnsMap={routeToColumnsMap} routeToResourceMap={routeToResourceMap}>
  //       {content}
  //     </PageProvider>
  //   );
  // }

  // Wrap with permissions provider if enabled
  if (enablePermissions) {
    content = (
      <PermissionsProvider>
        {content}
      </PermissionsProvider>
    );
  }

  // Wrap with language provider if enabled
  if (enableLanguage) {
    content = (
      <LanguageProvider>
        {content}
      </LanguageProvider>
    );
  }


  // Wrap with Redux provider if enabled
  if (useRedux && store) {
    content = (
      <ReduxProvider store={store}>
        {content}
      </ReduxProvider>
    );
  }

  // Wrap with auth provider if enabled
  if (enableAuth) {
    content = (
      <AuthProvider publicRoutes={publicRoutes}>
        {content}
      </AuthProvider>
    );
  }

   // Wrap with router if enabled
  if (enableRouter) {
    content = (
      <BrowserRouter>
        {content}
      </BrowserRouter>
    );
  }

  return content;
};

export default ERPUIProvider;